/**
 * FinaleChapterSelectScene — jump straight to Phase 4 after first clear (MY-1988).
 *
 * Reachable from EndingsGalleryScene once highestStageCleared >= 9.
 * Confirms the player's intent, then routes through CharSelectScene at the
 * final stage so they pick their hero before the game begins.
 */

import Phaser from 'phaser';
import { getSaveManager, ACTIVE_SLOT } from '@/systems/SaveManager';
import type { Difficulty } from '@/state/GameState';
import { GameState } from '@/state/GameState';
import { FINALE_STAGE } from '@/systems/finaleChapterSelect';

const FONT_MONO = '"Courier New", monospace';
const FONT_IMPACT = 'Impact, "Arial Black", sans-serif';

export interface FinaleChapterSelectInitData {
  slotIndex?: number;
}

export class FinaleChapterSelectScene extends Phaser.Scene {
  private slotIndex = ACTIVE_SLOT;

  constructor() {
    super({ key: 'FinaleChapterSelectScene' });
  }

  init(data: FinaleChapterSelectInitData): void {
    this.slotIndex = data.slotIndex ?? ACTIVE_SLOT;
  }

  create(): void {
    const { width, height } = this.scale;
    this.drawBackground(width, height);
    this.buildContent(width, height);
    this.bindKeys();
  }

  private drawBackground(width: number, height: number): void {
    this.add.rectangle(width / 2, height / 2, width, height, 0x080c12);
    // Panel frame
    this.add.rectangle(width / 2, height / 2, 760, 420, 0x0d1520).setStrokeStyle(1, 0x2a3240);
    // Red accent line at top of panel
    this.add.rectangle(width / 2, height / 2 - 210, 760, 3, 0xef233c);
  }

  private buildContent(width: number, height: number): void {
    this.add
      .text(width / 2, height * 0.24, 'FINALE REPLAY', {
        fontFamily: FONT_IMPACT,
        fontSize: '60px',
        color: '#ef233c',
        stroke: '#080c12',
        strokeThickness: 8,
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.38, 'PHASE 4: THE PM DECISION', {
        fontFamily: FONT_MONO,
        fontSize: '18px',
        color: '#ffba08',
        letterSpacing: 4,
      })
      .setOrigin(0.5);

    this.add
      .text(
        width / 2,
        height * 0.5,
        'Skip straight to the final confrontation.\nChoose your hero in Character Select.',
        {
          fontFamily: FONT_MONO,
          fontSize: '16px',
          color: '#9ea4a9',
          align: 'center',
          lineSpacing: 6,
        },
      )
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.66, '[ ENTER — SELECT HERO & BEGIN ]', {
        fontFamily: FONT_MONO,
        fontSize: '22px',
        color: '#ffffff',
        letterSpacing: 2,
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.76, '[ ESC — BACK TO GALLERY ]', {
        fontFamily: FONT_MONO,
        fontSize: '16px',
        color: '#555e6a',
        letterSpacing: 2,
      })
      .setOrigin(0.5);
  }

  private bindKeys(): void {
    const kb = this.input.keyboard!;
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.ENTER).on('down', () => this.onConfirm());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE).on('down', () => this.onConfirm());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.ESC).on('down', () => this.onBack());
    kb.addKey(Phaser.Input.Keyboard.KeyCodes.BACKSPACE).on('down', () => this.onBack());
  }

  private onConfirm(): void {
    GameState.newRun();
    const save = getSaveManager().load(this.slotIndex);
    if (save?.lastDifficulty) {
      GameState.setDifficulty(save.lastDifficulty as Difficulty);
    }
    GameState.resumeAtStage(FINALE_STAGE);
    this.scene.start('CharSelectScene', { resumeStage: FINALE_STAGE });
  }

  private onBack(): void {
    this.scene.start('EndingsGalleryScene', { slotIndex: this.slotIndex });
  }
}
